import React, { useContext, useEffect, useState } from "react";
import { createSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/*******INTERNAL IMPORTS********/
import { DisplaySpinnerContext } from "../../../context/ContextIndex";
import {
  DataGridTable,
  GridAction,
  ConfirmationPopup,
  notifyToast,
  ToastType,
  FormPage
} from "../../../components-old/ComponentsIndex";
import { PricingPortalAPI } from "../../../apis/api-index";
import { MdDelete, MdCreate } from "react-icons/md";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useSelector } from "react-redux";
import { selectSessionConfig } from "../../../store/store-selector";
import { AppUtility } from "../../../utils/utility";
import { saveItineraryTimes } from "../api-handler/api-handler";

const ItineraryTimesListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form state variables
  **************************************************/
  const [ItineraryTimesRecordList, setItineraryTimesRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [totalItineraryTimes, setTotalItineraryTimes] = useState(0);
  const [gridPageSizeOptions /*setGridPageSizeOptions*/] = useState([10, 20, 50]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);


  /**************************************************
  //Event definitions
  **************************************************/

  //on click of add icon for creating itinerary times
  const handleCreateItineraryTimesClick = () => {
    navigate("/ItineraryTimes");
  };

  //onclick of edit itinerary times
  const handleEditItineraryTimesClick = async (param) => {
    let _navigationData = param.recordResponse;
    delete _navigationData.CreatedDate;
    delete _navigationData.ModifiedDate;
    delete _navigationData.CountryCode;
    delete _navigationData.BusinessCode;
    delete _navigationData.Location;

    navigate(
      {
        pathname: "/itineraryTimes",
        search: createSearchParams({
          itineraryTimesId: param.recordResponse.ItineraryTimesId,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  //onclick of confirm of delete popup
  const handleConfirm = async () => {
    await deleteItineraryTimes(selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //on click of cancel of delete popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //onClick of delete in list
  const showDeleteItineraryTimesPopup = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //on page change
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //show review modal
  const handleShowSelectedItineraryTimesChanges = (event) => {
    let _navigationData = { previousPath: "/ItineraryTimeslist" };
    navigate(
      {
        pathname: "/reviewdetails",
        search: createSearchParams({
          reviewFilter: `&locationkey=${event.id}`,
        }).toString(),
      },
      { state: _navigationData }
    );
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/


  const setItineraryTimesList = (ItineraryTimesList, deviceTypeList) => {
    try {
      ItineraryTimesList.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
      let _ItineraryTimesList = ItineraryTimesList.map((item) => {
        return {
          id: item.ItineraryTimesId,
          itineraryTimesId: item.ItineraryTimesId,
          measurement: item.Measurement,
          jorney: item.Jorney,
          startRange: item.StartRange,
          endRange: item.EndRange,
          percentage1: item.Percentage1,
          percentage2: item.Percentage2,
          deviceType: deviceTypeList.find((x) => x.DeviceTypeId === item.DeviceTypeId)
            ? deviceTypeList.find((x) => x.DeviceTypeId === item.DeviceTypeId).TypeName
            : "",
          recordResponse: item,
        };
      });

      setItineraryTimesRecordList(_ItineraryTimesList);
      setTotalItineraryTimes(_ItineraryTimesList.length);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      spinnerCtx.setLoadingIndicator(false);
      alert(err);
    }
  };

  const FetchItineraryTimesList = async () => {
    try {
      let _lists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getItineraryTimesList",
        "itinerarytimes"
      );
      let _deviceTypeLists = await PricingPortalAPI.getList(
        settingsConfig.businessCode,
        settingsConfig.countryCode,
        "getDeviceType",
        "devicetype"
      );
      setItineraryTimesList(_lists, _deviceTypeLists);
    } catch (err) {
      alert(err);
    }
  };

  const formPreviousDataForDelete = (param) => {
    try {
      let _previousState = {};
      _previousState.Deleted = true;
      _previousState.DeviceType = param.deviceType;
      _previousState.Jorney = param.jorney;
      _previousState.Percentage1 = param.percentage1;
      _previousState.Percentage2 = param.percentage2;
      return _previousState;
    } catch (e) {
      alert(e);
    }
  };

  //Delete API call
  const deleteItineraryTimes = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let EditItineraryTimesId = param.id;
      const status = await saveItineraryTimes(formPreviousDataForDelete(param), {
        EditItineraryTimesId,
        settingsConfig
      });

      if (status?.responseStatus === 200) {
        notifyToast("Sent for Approval", ToastType.SUCCESS);
        await FetchItineraryTimesList();
      } else {
        notifyToast(status.errorMessage ? status.errorMessage : "Itinerary times deletion failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //JSX delcaration
  **************************************************/

  //data grid schema
  const dataGridSchema = [
    {
      field: "deviceType",
      headerName: t("itineraryTimesList.deviceType"),
      flex: 1,
    },
    {
      field: "measurement",
      headerName: t("itineraryTimesList.measurement"),
      flex: 1,
    },
    {
      field: "jorney",
      headerName: t("itineraryTimesList.jorney"),
      flex: 1,
    },
    {
      field: "startRange",
      headerName: t("itineraryTimesList.startRange"),
      flex: 1,
    },
    {
      field: "endRange",
      headerName: t("itineraryTimesList.endRange"),
      flex: 1,
    },
    {
      field: "percentage1",
      headerName: t("itineraryTimesList.percentage1"),
      flex: 1,
    },
    {
      field: "percentage2",
      headerName: t("itineraryTimesList.percentage2"),
      flex: 1,
    },
    {
      field: "recordResponse",
      hide: true,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <GridAction actionsConfig={actionConfigs} row={params.row} />
        );
      },
    },
  ];

  //button configs for add and download list
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateItineraryTimesClick,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create"
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(ItineraryTimesRecordList, "ItineraryTimeslist.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download"
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      onClick: handleShowSelectedItineraryTimesChanges,
      action: "review"
    },
    {
      icon: MdCreate,
      onClick: handleEditItineraryTimesClick,
      buttonColor: "#EEEEE",
      action: "edit"
    },
    {
      icon: MdDelete,
      onClick: showDeleteItineraryTimesPopup,
      buttonColor: "#A62B23",
      action: "delete"
    },
  ];


  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {

    spinnerCtx.setLoadingIndicator(true);
    FetchItineraryTimesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={ItineraryTimesRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalItineraryTimes}
        toolbarHeader={t("itineraryTimesList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteItineraryTimesConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default ItineraryTimesListForm;
